import React, { useState } from "react";
import { Box, Stack, CircularProgress } from "@mui/material";
import Modal, { ModalFoot } from "../../../../../component/Modal";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import { StatusChip } from "../../../../../component/listing";
import { tokens, displayFont, monoFamily } from "../../../../../theme";
import { approveExpense, rejectExpense } from "../../../../../services/expense";
import { useNotify } from "../../../../../component/NotificationProvider";
import { claimState, isOverPolicy } from "./expenseFacts";
import { isDecidable } from "./claimQueue";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
/** "12 March 2026" — spelled from a table; ICU renders "Sept" for September. */
const longDate = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  if (!m) return "—";
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
};

/** One fact about the claim: label left, value right, hairline under. */
const Row = ({ label, value, mono, last }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="baseline"
    spacing={2}
    sx={{ pb: last ? 0 : "0.5625rem", borderBottom: last ? "none" : `1px solid ${tokens.border}` }}
  >
    <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, flex: "none" }}>{label}</Box>
    <Box
      sx={{
        fontSize: mono ? "0.75rem" : "0.8125rem",
        fontFamily: mono ? monoFamily : "inherit",
        color: tokens.text,
        textAlign: "right",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value ?? "—"}
    </Box>
  </Stack>
);

/**
 * One claim, decided.
 *
 * It opens over the queue rather than replacing it, and deciding advances to
 * the next claim instead of returning to a list you then have to find your
 * place in. Approving twenty claims should cost twenty decisions.
 *
 * The amount is repeated inside the approve button because that is the number
 * being agreed to — the figure at the top is what was claimed, and the button
 * is what you are authorising.
 */
const ClaimDecisionPanel = ({ open, claim, money, position, total, onClose, onDecided }) => {
  const notify = useNotify();
  const [busy, setBusy] = useState("");

  if (!claim) return null;

  const state = claimState(claim);
  const decidable = isDecidable(claim);
  const overPolicy = isOverPolicy(claim);
  const limit = Number(claim?.limits?.claimLimit || 0);

  const decide = async (kind) => {
    setBusy(kind);
    try {
      const call = kind === "approve" ? approveExpense : rejectExpense;
      const res = await call({}, claim._id);
      if (res?.success === false) throw new Error(res?.message || `Could not ${kind} the claim`);
      notify.success(kind === "approve" ? "Claim approved" : "Claim rejected");
      onDecided?.(claim._id);
    } catch (e) {
      notify.error(e?.message || `Could not ${kind} the claim`);
    } finally {
      setBusy("");
    }
  };

  return (
    <Modal open={open} onClose={onClose} width="40rem">
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ p: "20px 22px", borderBottom: `1px solid ${tokens.border}` }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.1875rem", fontWeight: 700, color: tokens.text, mb: "3px" }}>
            {claim.category || "Claim"}
          </Box>
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
            {claim.employeeName} · submitted {longDate(claim.submittedAt)}
          </Box>
        </Box>
        <Box sx={{ textAlign: "right", flex: "none" }}>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.375rem", fontWeight: 700, fontVariantNumeric: "tabular-nums", color: tokens.text }}>
            {money(claim.amount)}
          </Box>
          <Box sx={{ mt: "0.25rem" }}>
            <StatusChip
              label={state === "overPolicy" ? "Over policy" : state}
              tone={state === "overPolicy" ? "danger" : state === "pending" ? "muted" : state === "approved" ? "success" : "danger"}
            />
          </Box>
        </Box>
      </Stack>

      <Stack direction="row" spacing={2.5} sx={{ p: "20px 22px" }}>
        {/* The receipt sits beside the facts, not behind a link: the whole
            question is usually whether the receipt matches the claim. */}
        <Box sx={{ width: "7.5rem", flex: "none" }}>
          {claim.receiptUrl ? (
            <Box
              component="a"
              href={claim.receiptUrl}
              target="_blank"
              rel="noreferrer"
              sx={{ display: "block", borderRadius: "6px", overflow: "hidden", border: `1px solid ${tokens.border}` }}
            >
              <Box component="img" src={claim.receiptUrl} alt="Receipt" sx={{ width: "100%", display: "block" }} />
            </Box>
          ) : (
            <Box
              sx={{
                height: "9.375rem",
                borderRadius: "6px",
                border: `1px dashed ${claim.missingReceipt ? tokens.dangerBorder : tokens.border}`,
                background: tokens.surface,
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
                pb: "0.625rem",
                fontFamily: monoFamily,
                fontSize: "0.625rem",
                color: claim.missingReceipt ? tokens.danger : tokens.textPlaceholder,
              }}
            >
              {claim.missingReceipt ? "none attached" : "no receipt"}
            </Box>
          )}
        </Box>

        <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
          <Row label="Category" value={claim.category} />
          <Row label="Date incurred" value={longDate(claim.submittedAt)} />
          {claim.claimType === "mileage" && claim.quantity ? (
            <Row label="Distance" value={`${claim.quantity} km`} />
          ) : null}
          <Row
            label="Policy limit"
            value={limit ? `${money(limit)} per claim` : "No limit set"}
          />
          <Row label="Claimed by" value={claim.employeeName} last />
        </Stack>
      </Stack>

      {claim.reason && (
        <Box sx={{ px: "22px", pb: "20px" }}>
          <Box sx={{ fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: tokens.textMuted, mb: "0.5rem" }}>
            Note from {String(claim.employeeName || "").split(" ")[0] || "the claimant"}
          </Box>
          <Box sx={{ fontSize: "0.8125rem", lineHeight: 1.6, color: tokens.text, background: tokens.surface, borderRadius: "8px", p: "12px 14px" }}>
            {claim.reason}
          </Box>
        </Box>
      )}

      {overPolicy && (
        <Box sx={{ px: "22px", pb: "20px" }}>
          <Box sx={{ fontSize: "0.8125rem", color: tokens.danger }}>
            This claim is over the category limit and has to be decided on its own —
            it cannot be part of a bulk approval.
          </Box>
        </Box>
      )}

      <ModalFoot note={total > 1 ? `Claim ${position} of ${total} awaiting a decision` : ""}>
        {decidable ? (
          <>
            <BtnSecondary
              onClick={() => decide("reject")}
              disabled={Boolean(busy)}
              sx={{ borderRadius: "8px", color: tokens.danger, borderColor: tokens.dangerBorder }}
            >
              {busy === "reject" ? <CircularProgress size={"1.1rem"} /> : "Reject"}
            </BtnSecondary>
            {/* The amount is repeated here because this is the number being
                agreed to, not the one being read. */}
            <BtnPrimary onClick={() => decide("approve")} disabled={Boolean(busy)} sx={{ borderRadius: "8px" }}>
              {busy === "approve" ? <CircularProgress size={"1.1rem"} sx={{ color: tokens.onPrimary }} /> : `Approve ${money(claim.amount)}`}
            </BtnPrimary>
          </>
        ) : (
          <BtnSecondary onClick={onClose} sx={{ borderRadius: "8px" }}>Close</BtnSecondary>
        )}
      </ModalFoot>
    </Modal>
  );
};

export default ClaimDecisionPanel;
